import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { useChannel } from "ably/react";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Comment } from "semantic-ui-react";
import { getWhatsappMessages, sendWhatsappMessage } from "../api/whatsapp";
import PageLoader from "../components/PageLoader";
import { MessageBubble } from "../components/messaging/MessageBubble";
import MessagesContainer from "../components/messaging/MessagesContainer";
import { ReplyField } from "../components/messaging/ReplyField";
import { style } from "../components/messaging/style";
import { useAuth } from "../contexts/AuthContext";

function WhatsappThread() {
  const { id } = useParams();
  const { apiUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [scrollPosition, setScrollPosition] = useState({ id: id, pos: 0 });

  const queryClient = useQueryClient();

  useChannel(`whatsapp:${apiUser.customer_id}:${id}`, (m) => {
    setMessages((prev) => [...prev, m.data]);
    setScrollPosition({ id: id, pos: 0 });
    document.getElementById("messagesContainer")?.scrollTo(0, 0);
  });

  const messageQuery = useInfiniteQuery({
    queryKey: ["whatsapp-messages", id],
    queryFn: async ({ pageParam = 1 }) => {
      const res = await getWhatsappMessages(id, {
        per_page: 30,
        page: pageParam,
      });
      // reverse data so we get the oldest chats at the top
      const reversed = res.data.data.reverse();
      res.data.data = reversed;
      return res;
    },
    onSuccess: (data) => {
      if (data.pages[data.pages.length - 1].data.meta.marker_updated) {
        queryClient.invalidateQueries({
          queryKey: ["whatsapp-threads"],
        });
      }
      setMessages([]);
    },
    getNextPageParam: (lastPage) =>
      lastPage.data.meta.current_page < lastPage.data.meta.last_page
        ? lastPage.data.meta.current_page + 1
        : undefined,
    keepPreviousData: true,
    staleTime: 10000,
  });

  useEffect(() => {
    const messagesContainer = document.getElementById("messagesContainer");
    id === scrollPosition.id
      ? messagesContainer?.scrollTo(0, scrollPosition.pos)
      : messagesContainer?.scrollTo(0, 0);
  });

  const getSenderDisplayName = (sender) => {
    switch (sender) {
      case apiUser.email:
        return "You";
      default:
        return sender;
    }
  };

  if (messageQuery?.isError) {
    toast("Failed to fetch messages", {
      type: "error",
      toastId: "fetch-error",
    });
    return null;
  }
  return (
    <MessagesContainer>
      {messageQuery.isFetching && !messageQuery.isFetchingNextPage ? (
        <PageLoader />
      ) : (
        <div style={style.container}>
          <div style={style.messages} id="messagesContainer">
            <Comment.Group style={style.messageGroup}>
              <div style={{ color: "#909199", textAlign: "center" }}>
                Images and other files are only available for 30 days after
                they're sent
              </div>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-around",
                  padding: "30px 0px",
                }}
              >
                {messageQuery.hasNextPage && (
                  <Button
                    className="whatsapp"
                    onClick={() => {
                      setScrollPosition({
                        id: id,
                        pos: document.getElementById("messagesContainer")
                          .scrollTop,
                      });
                      messageQuery.fetchNextPage();
                    }}
                    disabled={messageQuery.isFetching}
                    loading={messageQuery.isFetching}
                  >
                    Load more
                  </Button>
                )}
              </div>
              {messageQuery.data.pages.toReversed().map((page) => (
                <React.Fragment key={`page-${page.data.meta.current_page}`}>
                  {page.data.data.map((x) => {
                    return (
                      <MessageBubble
                        key={x.id}
                        byMe={x.user_name === apiUser.email ? true : false}
                        message={x.message}
                        messageType={x.media_type}
                        downloadUrl={x.media_type !== "text" && x.message}
                        fileName={x.media_type !== "text" && x.media_filename}
                        senderName={getSenderDisplayName(x.user_name)}
                        timestamp={x.created_at}
                        caption={x.media_caption}
                        highlightColor="whatsapp"
                      />
                    );
                  })}
                </React.Fragment>
              ))}
              {messages.map((x) => {
                return (
                  <MessageBubble
                    key={x.id}
                    byMe={x.user_name === apiUser.email ? true : false}
                    message={x.message}
                    messageType={x.media_type}
                    downloadUrl={x.media_type !== "text" && x.message}
                    fileName={x.media_type !== "text" && x.media_filename}
                    senderName={getSenderDisplayName(x.user_name)}
                    timestamp={x.created_at}
                    caption={x.media_caption}
                    highlightColor="whatsapp"
                  />
                );
              })}
            </Comment.Group>
          </div>
          <Reply
            timedOut={
              messageQuery?.data?.pages[messageQuery?.data?.pages.length - 1]
                .data.meta.timed_out
            }
            setMessages={setMessages}
            setScrollPosition={setScrollPosition}
          />
        </div>
      )}
    </MessagesContainer>
  );
}

function Reply({ timedOut, setMessages, setScrollPosition }) {
  const { id } = useParams();
  const { apiUser } = useAuth();
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");
  const queryClient = useQueryClient();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await sendWhatsappMessage(id, message);
      queryClient.invalidateQueries({
        queryKey: ["whatsapp-threads"],
      });
      setMessages((prev) => [
        ...prev,
        { user_name: apiUser.email, message: message, created_at: Date.now() },
      ]);
      setScrollPosition({ id: id, pos: 0 });
      document.getElementById("messagesContainer")?.scrollTo(0, 0);
    } catch {
      toast("Failed to send message", { type: "error", toastId: "send-error" });
    }
    setSending(false);
    setMessage("");
  };

  return (
    <ReplyField
      color="whatsapp"
      handleSubmit={handleSubmit}
      disabled={{
        status: timedOut,
        message:
          "You can't send a reply because the recipient hasn't messaged you recently",
      }}
      message={message}
      features={["media", "emoji"]}
      sending={sending}
      setMessage={setMessage}
    />
  );
}

export default WhatsappThread;
